/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { notificationService } from '../utils/notificationService';
import { useAuth } from '../contexts/AuthContext';
import CustomerHeader from '../components/CustomerHeader';

const OrderDetailsPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [previousStatus, setPreviousStatus] = useState<string | null>(null);
  const [statusChangeNotification, setStatusChangeNotification] = useState<string | null>(null);

  useEffect(() => {
    if (orderId) {
      loadOrderDetails();
    }
  }, [orderId]);

  // Auto-refresh order details every 10 seconds for better real-time updates
  // Auto-refresh disabled - order details will only refresh when user manually clicks refresh
  // useEffect(() => {
  //   if (!orderId) return;

  //   const interval = setInterval(() => {
  //     loadOrderDetails();
  //   }, 10000); // Refresh every 10 seconds

  //   return () => clearInterval(interval);
  // }, [orderId]);

  // Check for status changes and show notification
  useEffect(() => {
    if (order && lastUpdated) {
      const statusElement = document.querySelector('[data-order-status]');
      if (statusElement) {
        statusElement.classList.add('status-updated');
        setTimeout(() => {
          statusElement.classList.remove('status-updated');
        }, 2000);
      }
    }
  }, [order?.status, lastUpdated]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading order details for:', orderId);
      const orderData = await apiService.getOrder(orderId!);
      console.log('Order data received:', orderData);
      
      // Check if status changed
      if (previousStatus && previousStatus !== orderData.status) {
        console.log('Status changed from', previousStatus, 'to', orderData.status);
        setStatusChangeNotification(`Order status updated from ${previousStatus} to ${orderData.status}`);
        setTimeout(() => setStatusChangeNotification(null), 5000);
        
        // Create persistent notification for status change
        try {
          if (user?.id) {
            const orderNumber = orderData.order_number || orderData.id;
            const userId = user.id;
            
            console.log('🔔 OrderDetailsPage: Creating status change notification for user:', userId);
            
            await notificationService.notifyOrderStatusChange(
              orderData.id,
              orderNumber,
              previousStatus,
              orderData.status,
              userId
            );

            // Create specific notifications for shipped and delivered
            if (orderData.status === 'shipped') {
              await notificationService.notifyOrderShipped(
                orderData.id,
                orderNumber,
                orderData.tracking_number,
                userId
              );
            } else if (orderData.status === 'delivered') {
              await notificationService.notifyOrderDelivered(
                orderData.id,
                orderNumber,
                userId
              );
            } else if (orderData.status === 'cancelled') {
              await notificationService.notifyOrderCancelled(
                orderData.id,
                orderNumber,
                orderData.cancellation_reason,
                userId
              );
            }
          } else {
            console.warn('⚠️ OrderDetailsPage: Cannot create notification - no user ID');
          }
        } catch (notifError) {
          console.warn('Failed to create status change notification:', notifError);
          // Don't block the flow if notification creation fails
        }
      }
      
      setPreviousStatus(orderData.status);
      setOrder(orderData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading order details:', error);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'processing':
        return 'secondary';
      case 'shipped':
        return 'primary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'confirmed':
        return '✅';
      case 'processing':
        return '⚙️';
      case 'shipped':
        return '🚚';
      case 'delivered':
        return '📦';
      case 'cancelled':
        return '❌';
      default:
        return '❓';
    }
  };

  // Calculate subtotal from order items
  const calculateSubtotal = () => {
    if (!order?.items || !Array.isArray(order.items)) {
      return 0;
    }
    return order.items.reduce((sum: number, item: any) => {
      const quantity = item.quantity || 0;
      const unitPrice = parseFloat(item.unit_price) || 0;
      return sum + (quantity * unitPrice);
    }, 0);
  };

  // Handle reorder - add all items from order back to cart
  const handleReorder = async () => {
    if (!order?.items || !Array.isArray(order.items) || order.items.length === 0) {
      alert('No items to reorder');
      return;
    }

    try {
      // Get existing cart from localStorage
      const existingCart = localStorage.getItem('multiShopCart');
      let cart: Array<{ shop: any; items: Array<{ product: any; quantity: number }> }> = [];
      
      if (existingCart) {
        try {
          cart = JSON.parse(existingCart);
        } catch (e) {
          console.error('Error parsing cart:', e);
        }
      }

      // Get shop information - try to fetch shop details if not in order
      let shop = order.shop;
      const shopId = order.shop_id || order.shop?.id;
      
      if (!shop && shopId) {
        try {
          // Try to fetch shop details
          const shops = await apiService.getShops();
          shop = shops.find(s => String(s.id) === String(shopId));
        } catch (error) {
          console.warn('Could not fetch shop details:', error);
        }
      }

      // If still no shop, create minimal shop object
      if (!shop) {
        shop = {
          id: shopId || 'unknown',
          name: 'Shop',
          type: 'specialty',
          is_active: true
        };
      }

      // Group items by shop (in case order has items from multiple shops)
      const shopMap = new Map<string, { shop: any; items: any[] }>();

      for (const item of order.items) {
        const itemShopId = item.shop_id || shopId || order.shop_id || order.shop?.id;
        
        if (!itemShopId) {
          console.warn('Item missing shop_id:', item);
          continue;
        }

        // Get or create shop entry
        let shopCart = shopMap.get(String(itemShopId));
        
        if (!shopCart) {
          // Try to find shop in existing cart
          const existingShopCart = cart.find(c => c?.shop?.id === String(itemShopId));
          
          if (existingShopCart) {
            shopCart = { shop: existingShopCart.shop, items: [] };
          } else {
            // Use shop from order or fetch it
            let itemShop = shop;
            if (String(itemShopId) !== String(shopId)) {
              try {
                const shops = await apiService.getShops();
                itemShop = shops.find(s => String(s.id) === String(itemShopId)) || {
                  id: itemShopId,
                  name: 'Shop',
                  type: 'specialty',
                  is_active: true
                };
              } catch {
                itemShop = {
                  id: itemShopId,
                  name: 'Shop',
                  type: 'specialty',
                  is_active: true
                };
              }
            }
            shopCart = { shop: itemShop, items: [] };
          }
          shopMap.set(String(itemShopId), shopCart);
        }

        // Add item to shop cart
        const product = {
          id: String(item.product_id || item.id || ''),
          name: item.product_name || 'Product',
          price: parseFloat(item.unit_price) || 0,
          image: item.product_image || item.image || '📦',
          brand: item.brand || '',
          category: item.category || '',
          shopId: itemShopId,
          shop_id: itemShopId,
          inStock: true,
          stock: 100 // Assume in stock for reorder
        };

        // Check if product already exists
        const existingItem = shopCart.items.find(i => 
          String(i.product?.id) === String(product.id)
        );

        if (existingItem) {
          existingItem.quantity += (item.quantity || 1);
        } else {
          shopCart.items.push({
            product: product,
            quantity: item.quantity || 1
          });
        }
      }

      // Update cart with reordered items
      Array.from(shopMap.entries()).forEach(([shopIdKey, shopCart]) => {
        const existingShopCart = cart.find(c => c?.shop?.id === String(shopIdKey));
        
        if (existingShopCart) {
          // Merge items
          shopCart.items.forEach((newItem: { product: any; quantity: number }) => {
            const existingItem = existingShopCart.items.find(i => 
              String(i.product?.id) === String(newItem.product.id)
            );
            
            if (existingItem) {
              existingItem.quantity += newItem.quantity;
            } else {
              existingShopCart.items.push(newItem);
            }
          });
        } else {
          // Add new shop cart
          cart.push(shopCart);
        }
      });

      // Save to localStorage
      localStorage.setItem('multiShopCart', JSON.stringify(cart));
      
      // Show success message and navigate to cart
      alert('Items added to cart!');
      navigate('/app', { state: { showCart: true } });
    } catch (error) {
      console.error('Error reordering:', error);
      alert('Failed to add items to cart. Please try again.');
    }
  };

  if (loading) {
    return (
      <>
        <CustomerHeader
          variant="sticky"
          showTagline
          showVoiceSearch
          showProfileDropdown
          showBackButton
          onBackClick={() => navigate('/orders')}
          onSearch={(query) => {
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
          onCartClick={() => navigate('/cart')}
        />
        <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading order details...
          </Typography>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <CustomerHeader
          variant="sticky"
          showTagline
          showVoiceSearch
          showProfileDropdown
          showBackButton
          onBackClick={() => navigate('/orders')}
          onSearch={(query) => {
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
          onCartClick={() => navigate('/cart')}
        />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={loadOrderDetails}>
            Retry
          </Button>
        </Container>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <CustomerHeader
          variant="sticky"
          showTagline
          showVoiceSearch
          showProfileDropdown
          showBackButton
          onBackClick={() => navigate('/orders')}
          onSearch={(query) => {
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
          onCartClick={() => navigate('/cart')}
        />
        <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Order Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            The order you're looking for doesn't exist or has been removed.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/orders')}>
            View All Orders
          </Button>
        </Container>
      </>
    );
  }

  return (
    <>
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton
        onBackClick={() => navigate('/orders')}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/cart')}
      />
      <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Order Details
      </Typography>

      {/* Status Change Notification */}
      {statusChangeNotification && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setStatusChangeNotification(null)}
        >
          {statusChangeNotification}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Order Status */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Typography variant="h6" sx={{ mr: 2 }}>
                    {getStatusIcon(order.status)}
                  </Typography>
                  <Typography variant="h6">
                    Order #{order.order_number || order.id}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Chip
                    label={order.status.toUpperCase()}
                    color={getStatusColor(order.status)}
                    variant="outlined"
                    data-order-status
                    sx={{
                      transition: 'all 0.3s ease',
                      '&.status-updated': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 0 10px rgba(25, 118, 210, 0.5)',
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={loadOrderDetails}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <span>🔄</span>}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Placed on {new Date(order.created_at).toLocaleDateString()}
                {lastUpdated && (
                  <span> • Last updated: {lastUpdated.toLocaleTimeString()}</span>
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Items */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Items
              </Typography>
              <List>
                {order.items?.map((item: any, index: number) => (
                  <ListItem key={index} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        📦
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.product_name || 'Product'}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Quantity: {item.quantity} × ₹{item.unit_price}
                          </Typography>
                        </Box>
                      }
                    />
                    <Typography variant="subtitle1" fontWeight="bold">
                      ₹{(item.quantity * item.unit_price).toFixed(2)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Delivery Information */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Delivery Information
              </Typography>
              
              <Box mb={2}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  📍 Delivery Address
                </Typography>
                <Typography variant="body2">
                  {order.shipping_address?.street || 'Address not available'}
                </Typography>
                <Typography variant="body2">
                  {order.shipping_address?.city}, {order.shipping_address?.state} - {order.shipping_address?.pincode}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  💳 Payment Method
                </Typography>
                <Typography variant="body2">
                  {order.payment_method || 'Payment method not specified'}
                </Typography>
              </Box>

              {order.tracking_number && (
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    🚚 Tracking Number
                  </Typography>
                  <Typography variant="body2">
                    {order.tracking_number}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2">₹{calculateSubtotal().toFixed(2)}</Typography>
            </Box>
            
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Delivery Fee</Typography>
              <Typography variant="body2">₹{order.shipping_amount?.toFixed(2) || '0.00'}</Typography>
            </Box>
            
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="body2">Tax</Typography>
              <Typography variant="body2">₹{order.tax_amount?.toFixed(2) || '0.00'}</Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box display="flex" justifyContent="space-between" mb={3}>
              <Typography variant="h6" fontWeight="bold">
                Total
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                ₹{order.total_amount?.toFixed(2) || '0.00'}
              </Typography>
            </Box>

            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<span>🚚</span>}
              >
                Track Order
              </Button>
              
              <Button
                variant="contained"
                fullWidth
                startIcon={<span>🔄</span>}
                onClick={handleReorder}
              >
                Reorder
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      </Container>
    </>
  );
};

export default OrderDetailsPage;


